import { useState, useEffect } from "react";
import { X, ExternalLink, AlertTriangle, Info, Megaphone, Sparkles, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  image_url: string | null;
  link_url: string | null;
  link_label: string | null;
}

const TYPE_STYLES: Record<string, { icon: any; bg: string; border: string; iconColor: string }> = {
  info: { icon: Info, bg: "bg-blue-500/5", border: "border-blue-500/20", iconColor: "text-blue-500" },
  warning: { icon: AlertTriangle, bg: "bg-amber-500/5", border: "border-amber-500/20", iconColor: "text-amber-500" },
  alert: { icon: ShieldAlert, bg: "bg-destructive/5", border: "border-destructive/20", iconColor: "text-destructive" },
  promo: { icon: Megaphone, bg: "bg-emerald-500/5", border: "border-emerald-500/20", iconColor: "text-emerald-500" },
  news: { icon: Sparkles, bg: "bg-purple-500/5", border: "border-purple-500/20", iconColor: "text-purple-500" },
};

interface Props {
  restaurantId: string;
}

const AdminNotificationBanner = ({ restaurantId }: Props) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get active notifications
      const { data: notifs } = await supabase
        .from("admin_notifications")
        .select("id, title, message, type, image_url, link_url, link_label")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (!notifs || notifs.length === 0) return;

      // Get already dismissed
      const { data: dismissals } = await supabase
        .from("admin_notification_dismissals")
        .select("notification_id")
        .eq("restaurant_id", restaurantId)
        .eq("user_id", user.id);

      const dismissedIds = new Set((dismissals || []).map(d => d.notification_id));
      setNotifications((notifs as any as Notification[]).filter(n => !dismissedIds.has(n.id)));
    };
    load();
  }, [restaurantId]);

  const handleDismiss = async (notificationId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from("admin_notification_dismissals").insert({
      notification_id: notificationId,
      restaurant_id: restaurantId,
      user_id: user.id,
    });

    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleLinkClick = async (notificationId: string, url: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from("admin_notification_clicks").insert({
        notification_id: notificationId,
        restaurant_id: restaurantId,
        user_id: user.id,
      });
    }
    window.open(url, "_blank");
  };

  if (notifications.length === 0) return null;

  return (
    <div className="space-y-2">
      <AnimatePresence>
        {notifications.map(n => {
          const style = TYPE_STYLES[n.type] || TYPE_STYLES.info;
          const Icon = style.icon;

          return (
            <motion.div
              key={n.id}
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              className={`rounded-xl border ${style.border} ${style.bg} p-4 relative overflow-hidden`}
            >
              <div className="flex gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${style.iconColor} bg-background/50`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-sm">{n.title}</h4>
                    <button
                      onClick={() => handleDismiss(n.id)}
                      className="p-1 rounded-md hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{n.message}</p>
                  {n.image_url && (
                    <img src={n.image_url} alt="" className="mt-2 rounded-lg max-h-32 object-cover" />
                  )}
                  {n.link_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 h-7 text-xs gap-1.5"
                      onClick={() => handleLinkClick(n.id, n.link_url!)}
                    >
                      <ExternalLink className="h-3 w-3" />
                      {n.link_label || "Saiba mais"}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default AdminNotificationBanner;
